import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import './HeroImage.css';

const PHRASES_IT = [
  { highlight: 'stasera?',      subtitle: 'Concerti, aperitivi, mostre, teatro e mille eventi nella tua città. Gratis.' },
  { highlight: 'nel weekend?',  subtitle: 'Scopri i migliori eventi del fine settimana vicino a te. Gratis.' },
  { highlight: 'con gli amici?', subtitle: "Trova l'evento perfetto da condividere con chi ami." },
  { highlight: 'in città?',     subtitle: 'Dalla musica live ai mercatini: eventi per ogni gusto in tutta Italia.' },
  { highlight: 'di speciale?',  subtitle: 'Ogni giorno nuovi eventi da scoprire, tutti gratuiti e vicino a te.' },
  { highlight: 'questa sera?',  subtitle: "L'evento giusto è a un click di distanza. Scopri cosa succede fuori." },
];

const PHRASES_EN = [
  { highlight: 'tonight?',         subtitle: 'Concerts, aperitivos, art shows, theatre and thousands of events near you. Free.' },
  { highlight: 'this weekend?',    subtitle: 'Discover the best weekend events near you in Italy. Free.' },
  { highlight: 'with friends?',    subtitle: 'Find the perfect event to share with the people you love.' },
  { highlight: 'in the city?',     subtitle: 'From live music to markets: events for every taste across Italy.' },
  { highlight: 'special?',         subtitle: 'New events every day to discover — all free and near you.' },
  { highlight: 'this evening?',    subtitle: 'The right event is one click away. Discover what\'s happening outside.' },
];

const HeroImage = ({ ctaText, ctaLink, totalEvents }) => {
  const { i18n } = useTranslation();
  const isEn = i18n.language === 'en';
  const PHRASES = isEn ? PHRASES_EN : PHRASES_IT;
  const [idx, setIdx] = useState(0);
  const [fading, setFading] = useState(false);

  useEffect(() => {
    const timer = setInterval(() => {
      setFading(true);
      setTimeout(() => {
        setIdx(i => (i + 1) % PHRASES.length);
        setFading(false);
      }, 420);
    }, 4000);
    return () => clearInterval(timer);
  }, []);

  const current = PHRASES[idx];

  return (
    <div className="hero-container">
      <div className="hero-overlay" />

      <div className="w-100 hero-content">
        <div className="hero-eyebrow">
          {isEn ? '✨ Discover what to do tonight near you' : '✨ Scopri cosa fare stasera vicino a te'}
        </div>

        <h1 className="hero-title">
          {isEn ? 'What to do ' : 'Cosa fare '}{' '}
          <span className={`hero-animated-phrase${fading ? ' hero-phrase-out' : ' hero-phrase-in'}`}>
            {current.highlight}
          </span>
        </h1>

        <p className={`hero-subtitle${fading ? ' hero-phrase-out' : ' hero-phrase-in'}`}>
          {current.subtitle}
        </p>

        {ctaText && ctaLink && (
          <a href={ctaLink} className="hero-cta">
            {ctaText} →
          </a>
        )}

        {/* Feature pills */}
        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', justifyContent: 'center', marginTop: '1.25rem' }}>
          {(isEn ? [
            { icon: '🎉', label: 'RSVP to events' },
            { icon: '❤️', label: 'Match with attendees' },
            { icon: '💬', label: 'Live community' },
          ] : [
            { icon: '🎉', label: 'RSVP agli eventi' },
            { icon: '❤️', label: 'Match con chi ci va' },
            { icon: '💬', label: 'Community live' },
          ]).map((f, i) => (
            <span key={i} style={{
              background: 'rgba(255,255,255,0.1)', backdropFilter: 'blur(8px)',
              border: '1px solid rgba(255,255,255,0.18)', borderRadius: 20,
              padding: '0.35rem 0.85rem', fontSize: '0.8rem', fontWeight: 600,
              color: 'rgba(255,255,255,0.9)',
            }}>
              {f.icon} {f.label}
            </span>
          ))}
        </div>

        {/* Social proof */}
        <div style={{ marginTop: '1rem' }}>
          <Link to="/registrati" style={{
            fontSize: '0.82rem', color: 'rgba(255,255,255,0.65)',
            textDecoration: 'none', borderBottom: '1px solid rgba(255,255,255,0.25)',
          }}>
            {isEn ? '👥 Discover who\'s attending your events →' : '👥 Scopri chi partecipa ai tuoi eventi →'}
          </Link>
        </div>

        {/* Tutte le frasi nel DOM per l'indicizzazione SEO */}
        <ul className="hero-seo-phrases" aria-hidden="true">
          {PHRASES.map((p, i) => (
            <li key={i}>{isEn ? 'What to do' : 'Cosa fare'} {p.highlight} — {p.subtitle}</li>
          ))}
        </ul>
      </div>

      {totalEvents > 0 && (
        <div className="hero-stats">
          <div className="hero-stat">
            <div className="hero-stat-value">{totalEvents.toLocaleString('it')}+</div>
            <div className="hero-stat-label">{isEn ? 'events' : 'eventi'}</div>
          </div>
          <div className="hero-stat">
            <div className="hero-stat-value">100%</div>
            <div className="hero-stat-label">{isEn ? 'free' : 'gratuito'}</div>
          </div>
          <div className="hero-stat">
            <div className="hero-stat-value">🇮🇹</div>
            <div className="hero-stat-label">{isEn ? 'all Italy' : 'tutta italia'}</div>
          </div>
        </div>
      )}

      <div className="hero-bottom-wave">
        <svg viewBox="0 0 1440 60" preserveAspectRatio="none">
          <path d="M0,30 C360,60 1080,0 1440,30 L1440,60 L0,60 Z" fill="var(--bg)" />
        </svg>
      </div>
    </div>
  );
};

export default HeroImage;
